import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Breadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /**
   * Contenu complémentaire affiché sous le subtitle (typiquement métadonnées
   * enrichies de l'entité, ex: libellé NAF sous SIRET/SIREN sur la fiche
   * prospect). Rendu sur une ligne séparée, typographie discrète alignée
   * avec le subtitle.
   */
  extra?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  badges?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  extra,
  breadcrumbs,
  actions,
  badges,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6 flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="min-w-0 flex-1">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mb-2 flex items-center gap-1 text-xs text-text-muted">
            {breadcrumbs.map((b, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3 w-3 text-gray-400" />}
                {b.to ? (
                  <Link to={b.to} className="hover:text-brand">
                    {b.label}
                  </Link>
                ) : (
                  <span className="text-brand">{b.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-brand">{title}</h1>
          {badges}
        </div>
        {subtitle && <p className="mt-1 text-sm text-text-muted">{subtitle}</p>}
        {extra && <div className="mt-1 text-sm text-text-muted">{extra}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
